import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavController } from '@ionic/angular';
import { BehaviorSubject, Observable, tap, from, switchMap, of, map, catchError, finalize, shareReplay } from 'rxjs';
import { baseUrl } from 'src/environments/environment';
import { GeneralService } from './general.service';
import { endV2Session } from './v2/v2-api.service';

@Injectable({
  providedIn: 'root'
})
export class TokenService {

  isAuthenticated: BehaviorSubject<boolean> = new BehaviorSubject<any>(null);
  currentAccessToken = localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY');

  constructor(private http: HttpClient, private nav: NavController, private generalService: GeneralService) {
    // this.refreshToken()
  }

  storeToken(accessToken, refreshToken) {
    this.currentAccessToken = accessToken;
    localStorage.setItem('ALTOMATE_ACCESS_TOKEN_KEY', accessToken)
    localStorage.setItem('ALTOMATE_REFRESH_TOKEN_KEY', refreshToken)
    this.isAuthenticated.next(true);
  }


  async loadToken() {
    const token: any = await localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY');
    if (token) {
      this.currentAccessToken = token;
      this.isAuthenticated.next(true);
    } else {
      this.isAuthenticated.next(false);
    }
  }

  logout() {
    endV2Session();
    localStorage.removeItem('ALTOMATE_ACCESS_TOKEN_KEY')
    localStorage.removeItem('ALTOMATE_REFRESH_TOKEN_KEY')
    this.isAuthenticated.next(false);
    this.nav.navigateRoot('login', { animated: true, animationDirection: 'back' })
    this.generalService.showToast('Your session was expired! Please login again.', 'error', 'bottom')

  }

  // Load the refresh token from storage
  // then attach it as the header for one specific API call
  refreshToken() {
    const refreshToken: any = from([localStorage.getItem('ALTOMATE_REFRESH_TOKEN_KEY')]);


    return refreshToken.pipe(
      switchMap((token: any) => {
        if (token) {

          const httpOptions = {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            })
          }

          return this.http.post(`${baseUrl}/refresh-id-token`, {}, httpOptions);
        } else {
          // No stored refresh token
          return of(null);
        }
      })
    );
  }

  private freshToken$: Observable<void> | null = null;

  /**
   * Refresh the access token first when it has expired or expires within 2 minutes.
   * The JWT interceptor only refreshes after the API answers 403, but some servers (the AI
   * agent) forward the token to the API themselves, so that 403 never reaches the browser.
   * Partner sessions use a different token and are left as they are.
   */
  ensureFreshToken(): Observable<void> {
    const usesPartnerToken = !this.generalService.isNativePlatform() && !this.generalService.isAltomateLink();
    const token = localStorage.getItem('ALTOMATE_ACCESS_TOKEN_KEY');
    if (usesPartnerToken || !token || !this.expiresSoon(token)) return of(undefined);
    if (!this.freshToken$) {
      this.freshToken$ = this.refreshToken().pipe(
        map((res: any) => {
          if (res?.success === true && res.idToken && res.refreshToken) {
            localStorage.setItem('ALTOMATE_REFRESH_TOKEN_KEY', res.refreshToken);
            this.currentAccessToken = res.idToken;
            localStorage.setItem('ALTOMATE_ACCESS_TOKEN_KEY', res.idToken);
          }
        }),
        // Let the request go ahead anyway; the API still decides whether the session is valid.
        catchError(() => of(undefined)),
        finalize(() => (this.freshToken$ = null)),
        shareReplay(1)
      ) as Observable<void>;
    }
    return this.freshToken$;
  }

  private expiresSoon(token: string, marginMs = 2 * 60 * 1000): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return !payload?.exp || payload.exp * 1000 - Date.now() < marginMs;
    } catch {
      return false;
    }
  }

  // Store a new access token
  storeAccessToken(accessToken: any) {
    this.currentAccessToken = accessToken
    localStorage.setItem('ALTOMATE_ACCESS_TOKEN_KEY', this.currentAccessToken)
    return from([localStorage.setItem('ALTOMATE_ACCESS_TOKEN_KEY', this.currentAccessToken)] as any);
  }
}
